import type { FunctionComponent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';
import { TrelloIcon } from '@atlaskit/logo';
import Tile from '@atlaskit/tile';

import McpPermissions from './McpPermissions/McpPermissions';
import AgentMcpCapabilitiesWarning from './AgentMcpCapabilitiesWarning/AgentMcpCapabilitiesWarning';
import SiteSelector from './SiteSelector/SiteSelector';
import BitbucketWorkspaceSelector from './BitbucketWorkspaceSelector/BitbucketWorkspaceSelector';
import TrelloWorkspaceSelector from './TrelloWorkspaceSelector/TrelloWorkspaceSelector';
import Headline from '../shared/agent-consent/Headline';
import McpInfoCard from './McpInfoCard/McpInfoCard';
import McpDetails from './McpDetails/McpDetails';
import McpBanners from './McpBanners/McpBanners';
import McpPolicyAlerts from './McpPolicyAlerts/McpPolicyAlerts';
import SiteBypassBanner from '../shared/agent-consent/SiteBypassBanner/SiteBypassBanner';
import { useMcpContext } from './McpContextProvider';
import { useMcpBranding } from './mcpTerminology';
import { useInfo } from '../../Flow/ConsentContextProvider';
import {
  getForgeMcpAppIcon,
  getForgeMcpAppName,
} from '#definitions/consent-info';
import type { AgentMcpDisplayMetadata } from './domain/useAgentMcpMetadata';

const forgeIconImageStyles = xcss({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const rootStyles = xcss({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  [media.above.md]: {
    maxWidth: '1280px',
  },
});

const panelsRowStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  flex: '1 1 auto',
  minHeight: '0',
  width: '100%',
  flexWrap: 'wrap',
  [media.above.sm]: {
    minHeight: 'calc(100vh - 56px)',
  },
});

const leftPanelStyles = xcss({
  boxSizing: 'border-box',
  display: 'flex',
  width: '100%',
  minWidth: '100%',
  flexShrink: 0,
  paddingInline: 'space.200',
  paddingBlockStart: 'space.800',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space.500',
  position: 'static',
  top: 'space.0',
  alignSelf: 'flex-start',
  overflowY: 'visible',
  [media.above.sm]: {
    padding: 'space.0',
    paddingBlockStart: 'space.400',
    marginInline: 'space.300',
    marginBlockStart: 'space.400',
    marginBlockEnd: 'space.800',
    width: '300px',
    minWidth: 'min(100%, 300px)',
    position: 'sticky',
    maxHeight: '100vh',
    overflowY: 'auto',
  },
  [media.above.md]: {
    marginInline: 'space.600',
    width: '350px',
    minWidth: 'min(100%, 350px)',
  },
  [media.above.lg]: {
    marginInline: 'space.800',
  },
});

const rightPanelStyles = xcss({
  height: 'auto',
  boxSizing: 'border-box',
  display: 'flex',
  paddingInline: 'space.200',
  paddingBlockStart: 'space.800',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space.500',
  flex: '1 0 100%',
  width: '100%',
  minWidth: '100%',
  borderLeftWidth: 0,
  borderLeftStyle: 'solid',
  borderLeftColor: 'color.border',
  // @ts-expect-error — 500px is an intentional custom value not in design tokens
  paddingBlockEnd: '500px',
  [media.above.sm]: {
    paddingInline: 'space.300',
    flex: '1 1 0%',
    minWidth: '0',
    borderLeftWidth: 'border.width',
    // @ts-expect-error — 120px is an intentional custom value not in design tokens
    paddingBlockEnd: '120px',
  },
  [media.above.md]: {
    paddingInline: 'space.600',
  },
  [media.above.lg]: {
    paddingInline: 'space.800',
  },
});

type MCPProps = {
  /** Loaded agent metadata; present only for Agent MCP consent. */
  agentMetadata?: AgentMcpDisplayMetadata;
};

const MCP: FunctionComponent<MCPProps> = ({ agentMetadata }) => {
  const { forgeMcpConsentTarget } = useInfo();
  const {
    scopeClassification,
    trelloRequestedButNoAccess,
    availableTrelloWorkspaces,
    hasTrelloInboxPlannerScopes,
  } = useMcpContext();
  const { product } = useMcpBranding();
  const showTrelloWorkspaceSelector =
    scopeClassification.hasTrello || trelloRequestedButNoAccess;
  const isTrelloOnly =
    showTrelloWorkspaceSelector && !scopeClassification.hasAtlassian;
  // Signed in to an account with no Trello workspaces and nothing else to
  // grant: the panel is a dead end, so the app details are noise.
  const isTrelloOnlyDeadEnd =
    isTrelloOnly &&
    availableTrelloWorkspaces.length === 0 &&
    !hasTrelloInboxPlannerScopes;
  const trelloOnlyIcon = isTrelloOnly ? (
    <TrelloIcon size="xlarge" appearance="brand" />
  ) : undefined;
  const forgeAppName = getForgeMcpAppName({ forgeMcpConsentTarget });
  const forgeAppIcon = getForgeMcpAppIcon({ forgeMcpConsentTarget });
  const agentIcon = agentMetadata?.icon ? (
    <Tile label={agentMetadata.name} size="xlarge" isInset={false}>
      <Box
        as="img"
        src={agentMetadata.icon}
        alt=""
        xcss={forgeIconImageStyles}
      />
    </Tile>
  ) : undefined;
  const targetIcon = agentMetadata ? (
    agentIcon
  ) : forgeAppName ? (
    <Tile label={forgeAppName} size="xlarge" isInset={false}>
      {forgeAppIcon ? (
        <Box as="img" src={forgeAppIcon} alt="" xcss={forgeIconImageStyles} />
      ) : undefined}
    </Tile>
  ) : (
    trelloOnlyIcon
  );

  return (
    <Box xcss={rootStyles}>
      <Box xcss={panelsRowStyles}>
        <Box xcss={leftPanelStyles}>
          <Headline
            icon={targetIcon}
            product={product}
            agentName={agentMetadata?.name}
            agentDescription={agentMetadata?.description}
          />
          <McpInfoCard />
        </Box>
        <Box xcss={[rightPanelStyles]}>
          <McpBanners />
          {!trelloRequestedButNoAccess && !isTrelloOnlyDeadEnd && (
            <McpDetails />
          )}
          {agentMetadata ? (
            <AgentMcpCapabilitiesWarning agentName={agentMetadata.name} />
          ) : (
            <>
              <SiteBypassBanner />
              {scopeClassification.hasAtlassian && <SiteSelector />}
              <BitbucketWorkspaceSelector />
              {showTrelloWorkspaceSelector && (
                <TrelloWorkspaceSelector showHeading={isTrelloOnly} />
              )}
              <McpPolicyAlerts />
              <McpPermissions />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MCP;
